import { ReactNode, createContext, useCallback, useContext, useMemo, useState } from 'react';

/**
 * Global Loading State Interface
 */
interface GlobalLoadingState {
  isLoading: boolean;
  message: string;
  size: 'sm' | 'md' | 'lg';
}

/**
 * Global Loading Actions Interface
 */
interface GlobalLoadingActions {
  showLoading: (message?: string, size?: 'sm' | 'md' | 'lg') => void;
  hideLoading: () => void;
}

/**
 * Global Loading Context Type
 */
export type GlobalLoadingContextType = GlobalLoadingState & GlobalLoadingActions;

/**
 * Create global loading context
 */
const GlobalLoadingContext = createContext<GlobalLoadingContextType | undefined>(undefined);

/**
 * Global Loading Provider Props
 */
interface GlobalLoadingProviderProps {
  children: ReactNode;
}

/**
 * Global Loading Provider Component
 * Provides loading state that can be accessed from anywhere in the app
 */
export function GlobalLoadingProvider({ children }: GlobalLoadingProviderProps) {
  const [state, setState] = useState<GlobalLoadingState>({
    isLoading: false,
    message: 'Loading...',
    size: 'md',
  });

  const showLoading = useCallback((message?: string, size?: 'sm' | 'md' | 'lg') => {
    
    setState({
      isLoading: true,
      message: message || 'Loading...',
      size: size || 'md',
    });
  }, []);

  const hideLoading = useCallback(() => {
    
    setState(prev => ({ ...prev, isLoading: false }));
  }, []);

  const contextValue: GlobalLoadingContextType = useMemo(() => ({
    ...state,
    showLoading,
    hideLoading,
  }), [state, showLoading, hideLoading]);

  return (
    <GlobalLoadingContext.Provider value={contextValue}>
      {children}
    </GlobalLoadingContext.Provider>
  );
}

/**
 * Hook to use global loading context
 * @returns Global loading context value
 * @throws Error if used outside GlobalLoadingProvider
 */
export function useGlobalLoading(): GlobalLoadingContextType {
  const context = useContext(GlobalLoadingContext);
  if (context === undefined) {
    throw new Error('useGlobalLoading must be used within a GlobalLoadingProvider');
  }
  return context;
}

/**
 * Hook to use global loading context safely (no error if outside provider)
 * @returns Global loading context value or default state
 */
export function useGlobalLoadingSafe(): GlobalLoadingContextType {
  const context = useContext(GlobalLoadingContext);
  if (context === undefined) {
    // Return default state if context is not available
    return {
      isLoading: false,
      message: 'Loading...',
      size: 'md',
      showLoading: () => {},
      hideLoading: () => {},
    };
  }
  return context;
}